import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { ErrorMessage, LoadingSpinner, ModalDialog } from "@msys/ui";
import { Button } from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import React from "react";
import { SplashScreen } from "../../../../common/SplashScreen.js";
import { useAuth } from "../../../auth/useAuth.js";
import { ErrorBoundary } from "../../../commons/ErrorBoundary.js";
import { assertNever } from "../../../utils.js";
import { useCategories } from "../../categories/useCategories.js";
import {
  useOnboardingFlowModalQuery,
  type OnboardingFlowModalQuery,
} from "./OnboardingFlowModal.generated.js";
import type { OnboardingFlowStage } from "./onboardingFlowStage.js";
import { OnboardingFlowFormStage } from "./OnboardingFlowFormStage.js";
import { OnboardingFlowIntroStage } from "./OnboardingFlowIntroStage.js";
import { OnboardingFlowShell } from "./OnboardingFlowShell.js";
import { OnboardingFlowSuccessStage } from "./OnboardingFlowSuccessStage.js";
import { OnboardingFlowVerticalsStage } from "./OnboardingFlowVerticalsStage.js";
import { ONBOARDING_COLORS } from "./onboardingDesign.js";
import { onboardingFlowWizardReducer } from "./onboardingFlowWizardReducer.js";
import {
  initialOnboardingFlowWizardState,
  initialWizardStateFromSuggestions,
  type OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";

export {
  VALID_ONBOARDING_FLOW_STAGES,
  type OnboardingFlowStage,
} from "./onboardingFlowStage.js";

interface Props {
  stage: OnboardingFlowStage;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

export type OnboardingViewer = Extract<
  OnboardingFlowModalQuery["viewer"],
  { __typename: "Viewer" }
>;

// Every namespace the stages use. With `useSuspense: false` one that has not
// arrived renders its English keys, so the wizard waits for them to open in the
// user's language right away.
const ONBOARDING_NAMESPACES = [
  "Address",
  "AttachmentField",
  "Global",
  "OrganisationProfile",
  "PhonesField",
  "ProjectTypes",
];

/**
 * Whether the namespaces have loaded, waited for through Tolgee's own promise:
 * `useTranslate`'s `isLoading` can miss the load finishing between its render
 * and its subscription and then stays `true`. A failed load counts as done, so
 * the wizard opens with the keys rather than not at all.
 */
function useTranslationsReady(namespaces: string[]): boolean {
  const tolgee = useTolgee();
  const [ready, setReady] = React.useState(() => tolgee.isLoaded(namespaces));
  React.useEffect(() => {
    if (ready) {
      return;
    }
    let active = true;
    const markReady = () => {
      if (active) {
        setReady(true);
      }
    };
    // `forget`: only load them; `useTranslate` keeps them active.
    tolgee.addActiveNs(namespaces, true).then(markReady, markReady);
    return () => {
      active = false;
    };
  }, [namespaces, ready, tolgee]);
  return ready;
}

export const OnboardingFlowModal = ({ stage, setStage }: Props) => {
  const client = useApolloClient();
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const translationsReady = useTranslationsReady(ONBOARDING_NAMESPACES);
  const query = useOnboardingFlowModalQuery({ client });
  const {
    categories,
    loaded: categoriesLoaded,
    loading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useCategories();
  const viewer = getDataOrNull(query.data?.viewer);

  // The wizard starts from the organisation's suggested offered services
  // (MEIS-13566 design D13), so nothing renders until the fresh viewer
  // response and the taxonomy the mapping needs have loaded. The start state
  // is computed once, so a later refetch neither remounts the content nor
  // drops the user's wizard state. Cached data is not enough: with
  // `cache-and-network` it can be stale while the new response is still
  // loading; the same holds for the categories, so a refresh in flight or
  // failed keeps the wizard closed. So do translations still on their way;
  // once open, a later language switch does not close it again. Only
  // CRAFTSMAN organisations walk the trades step: any other type starts with
  // the default wizard state and neither waits for nor fails on the taxonomy.
  const initialWizardStateRef = React.useRef<OnboardingFlowWizardState | null>(
    null
  );
  const needsTaxonomy = viewer?.organisation.organisationType === "CRAFTSMAN";
  // A failed refresh keeps `query.data` as the cached viewer, so the error must
  // also keep the wizard closed (Retry) rather than lock in stale suggestions.
  if (
    initialWizardStateRef.current === null &&
    viewer &&
    !query.loading &&
    !query.error &&
    translationsReady
  ) {
    if (!needsTaxonomy) {
      initialWizardStateRef.current = initialOnboardingFlowWizardState;
    } else if (categoriesLoaded && !categoriesLoading && !categoriesError) {
      initialWizardStateRef.current = initialWizardStateFromSuggestions(
        viewer.organisation.suggestedOfferedServices,
        categories
      );
    }
  }
  const initialWizardState = initialWizardStateRef.current;

  // One dialog from the spinner to the wizard: swapping a loading dialog for
  // the wizard's own unmounted one and faded in the other, so the backdrop
  // flashed twice right after the signup.
  if (!initialWizardState || !viewer) {
    const failed =
      !!query.error ||
      (!query.loading && !viewer) ||
      (needsTaxonomy &&
        (!!categoriesError || (!categoriesLoading && !categoriesLoaded)));
    return (
      <OnboardingFlowDialog
        stage={stage}
        organisationType={viewer?.organisation.organisationType}
        loading={!failed}
      >
        {failed ? (
          <ErrorMessage
            message={t("Something went wrong. Please try again.", {
              ns: "Global",
            })}
            button={
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  void query.refetch();
                  void refetchCategories();
                }}
              >
                {t("Retry", { ns: "Global" })}
              </Button>
            }
          />
        ) : stage === "intro" ? (
          // The intro follows the signup: the app's account preparation stays
          // on screen until the intro paints.
          <SplashScreen
            message={t("Preparing account ...", { ns: "Global" })}
          />
        ) : (
          <LoadingSpinner />
        )}
      </OnboardingFlowDialog>
    );
  }

  return (
    <OnboardingFlowDialog
      stage={stage}
      organisationType={viewer.organisation.organisationType}
    >
      <OnboardingFlowModalContent
        stage={stage}
        setStage={setStage}
        viewer={viewer}
        initialWizardState={initialWizardState}
      />
    </OnboardingFlowDialog>
  );
};

/**
 * The wizard's dialog: full screen, or the centred done card over the dimmed
 * app. It stays the same element while the stage or the loading state changes.
 */
const OnboardingFlowDialog = ({
  stage,
  organisationType,
  loading = false,
  children,
}: {
  stage: OnboardingFlowStage;
  /** While loading, a full-screen dialog shows the app's background, as the splash before it. */
  loading?: boolean;
  /** `undefined` while the viewer loads; the dialog is named once it is known. */
  organisationType:
    | OnboardingViewer["organisation"]["organisationType"]
    | undefined;
  children: React.ReactNode;
}) => {
  const { t } = useTranslate(["OrganisationProfile"]);
  const isCardStage = stage === "success";

  // The name goes on the paper (the element with role="dialog"): an aria-label
  // in the dialog props would land on MUI's outer wrapper instead.
  const dialogName =
    organisationType === undefined
      ? undefined
      : isCardStage
        ? t("Meister Systems is ready", { ns: "OrganisationProfile" })
        : organisationType !== "CRAFTSMAN"
          ? t("Set up your account", { ns: "OrganisationProfile" })
          : t("Set up your business", { ns: "OrganisationProfile" });

  // The dialog focuses its container when it opens; that programmatic focus
  // must not draw a ring around the whole viewport. Controls inside keep
  // their own focus-visible outlines.
  const noFocusRing = {
    outline: "none",
    "& .MuiDialog-container": { outline: "none" },
    "& .MuiDialog-paper": { outline: "none" },
  };

  return (
    <ModalDialog
      // Modals opened from a step (the matrix) stack above the wizard; without
      // this the modal stack hides the wizard behind them.
      alwaysVisible
      maxWidth={isCardStage ? false : "sm"}
      dialogProps={
        isCardStage
          ? {
              sx: {
                ...noFocusRing,
                "& .MuiBackdrop-root": {
                  backgroundColor: ONBOARDING_COLORS.backdrop,
                },
              },
              slotProps: {
                paper: {
                  "aria-label": dialogName,
                  // Below `md` ModalDialog makes every dialog full-screen, so the
                  // card's size only applies from there up.
                  sx: {
                    width: { md: 640 },
                    maxWidth: { md: "calc(100% - 32px)" },
                    m: { md: 2 },
                    borderRadius: { md: "16px" },
                    boxShadow: "0 24px 64px rgba(14, 30, 67, 0.35)",
                  },
                },
              },
            }
          : {
              fullScreen: true,
              // A full-screen stage replaces the screen like a page; fading it
              // in over the app only showed a grey backdrop first.
              transitionDuration: 0,
              sx: noFocusRing,
              slotProps: {
                paper: {
                  "aria-label": dialogName,
                  ...(loading && { sx: { bgcolor: "background.default" } }),
                },
              },
            }
      }
    >
      {children}
    </ModalDialog>
  );
};

interface ContentProps extends Props {
  viewer: OnboardingViewer;
  initialWizardState: OnboardingFlowWizardState;
}

const OnboardingFlowModalContent = ({
  stage,
  setStage,
  viewer,
  initialWizardState,
}: ContentProps) => {
  // Wizard state lives at the modal root so it survives back/forward
  // navigation between the stage components. State is dropped when the
  // modal unmounts (i.e. when the user closes mid-flow).
  const [wizardState, wizardDispatch] = React.useReducer(
    onboardingFlowWizardReducer,
    initialWizardState
  );

  // Whether the owner saved "Your property" (not skipped it); the done screen
  // words itself after it. A reload on the done screen forgets it, which falls
  // back to the neutral wording.
  const [propertySaved, setPropertySaved] = React.useState(false);

  const { auth } = useAuth();

  const hasCallAssistant =
    viewer.organisation.capabilities.includes("callAssistant");

  // The company email defaults to the address the user signed up with.
  const signedUpEmail =
    typeof auth.idTokenParsed?.email === "string"
      ? auth.idTokenParsed.email
      : null;

  return (
    <ErrorBoundary>
      {stage === "success" ? (
        <OnboardingFlowSuccessStage
          organisationType={viewer.organisation.organisationType}
          hasCallAssistant={hasCallAssistant}
          propertySaved={propertySaved}
          setStage={setStage}
        />
      ) : (
        <OnboardingFlowShell
          stage={stage}
          organisationType={viewer.organisation.organisationType}
          setStage={setStage}
        >
          {stage === "intro" ? (
            <OnboardingFlowIntroStage
              organisationType={viewer.organisation.organisationType}
              setStage={setStage}
            />
          ) : stage === "form" ? (
            <OnboardingFlowFormStage
              organisation={viewer.organisation}
              signedUpEmail={signedUpEmail}
              onSaved={() => setPropertySaved(true)}
              setStage={setStage}
            />
          ) : stage === "verticals" ? (
            <OnboardingFlowVerticalsStage
              viewer={viewer}
              state={wizardState}
              dispatch={wizardDispatch}
              setStage={setStage}
            />
          ) : (
            assertNever(stage)
          )}
        </OnboardingFlowShell>
      )}
    </ErrorBoundary>
  );
};
